import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';

interface Props {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  lastUpdated?: string;
  children: ReactNode;
  toc?: Array<{ id: string; label: string }>;
}

/**
 * Shared chrome for every public document page (legal, security, about,
 * contact, etc). Glass header card, optional sidebar table-of-contents,
 * consistent prose typography, "Back to home" affordance.
 */
export function LegalShell({ title, subtitle, eyebrow, lastUpdated, children, toc }: Props) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero band */}
        <section className="relative isolate overflow-hidden pt-16 pb-10">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 gradient-mesh" />
            <motion.div
              className="absolute -top-32 -start-32 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-indigo-400/30 to-fuchsia-400/25 blur-3xl"
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
          <div className="relative mx-auto max-w-4xl px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition mb-4">
              <ArrowLeft className="h-3.5 w-3.5" /> {t('legal.backHome')}
            </Link>
            {eyebrow && (
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{eyebrow}</p>
            )}
            <h1 className="display serif-jp mt-1 text-3xl md:text-5xl font-semibold tracking-tight text-balance">{title}</h1>
            {subtitle && <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">{subtitle}</p>}
            {lastUpdated && (
              <p className="mt-3 text-xs text-muted-foreground">
                {t('legal.lastUpdated', { date: lastUpdated })}
              </p>
            )}
          </div>
        </section>

        {/* Body */}
        <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {toc && toc.length > 0 && (
              <aside className="hidden lg:block lg:col-span-3">
                <div className="sticky top-24 rounded-2xl border border-border bg-card/60 p-4">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3">
                    {t('legal.onThisPage')}
                  </p>
                  <ul className="space-y-2 text-sm">
                    {toc.map((item) => (
                      <li key={item.id}>
                        <a href={`#${item.id}`} className="text-foreground/70 hover:text-primary transition">
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            )}

            <article className={`prose-doc ${toc ? 'lg:col-span-9' : 'lg:col-span-12 max-w-3xl mx-auto'}`}>
              {children}
            </article>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

/* ── Inline typography helpers used by every legal page ─────────── */

export function DocSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 mt-10 first:mt-0">
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-balance">{title}</h2>
      <div className="mt-3 space-y-3 text-sm md:text-base text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-pretty">{children}</p>;
}

export function UL({ children }: { children: ReactNode }) {
  return <ul className="list-disc ps-5 space-y-1.5 marker:text-muted-foreground">{children}</ul>;
}

export function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}

export function Code({ children }: { children: ReactNode }) {
  return <code className="num text-[0.85em] rounded bg-muted px-1 py-0.5">{children}</code>;
}
